import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue, useResetRecoilState } from 'recoil'
import {
  ModalBody,
  FormElement,
  DatePicker,
  Select,
  InputNumber,
  MessageBox,
  MESSAGE_BOX_VARIANT_DANGER
} from 'mailup-web-components'
import { Foot } from './Foot'
import { getLocaleDate, zones } from 'Helpers/dates'
import { STORE_KEY_LIST } from 'Constants'
import { useStorage } from 'Hooks/'

import moment from 'moment-timezone'
import { modalSchedulingState } from 'Pages/EmailSendingWizard/recoil/state/modals'
import { sendingScheduleSetScheduleCallback } from 'Pages/EmailSendingWizard/recoil/callbacks/callback'
import { useHistory } from 'react-router-dom'

const INPUT_MINUTES_STEP = 5
const roundToClosestMultiple = (value, n = 1) => Math.ceil(value / n) * n

export const ContentScheduled = () => {
  const history = useHistory()
  const intl = useIntl()
  const [{ TimeZoneCode: listTimeZoneCode }] = useStorage(STORE_KEY_LIST).values

  const confirm = useRecoilCallback(sendingScheduleSetScheduleCallback, [])
  const cancel = useResetRecoilState(modalSchedulingState)

  const [selectTimezone, setSelectTimezone] = useState('')

  const {
    date: scheduleDate,
    timeZoneCode: scheduleTimeZoneCode
  } = useRecoilValue(modalSchedulingState)

  const [timeZone, setTimeZone] = useState(
    scheduleTimeZoneCode || listTimeZoneCode
  )
  const [oldTimeZone, setOldTimeZone] = useState(
    scheduleTimeZoneCode || listTimeZoneCode
  )
  const [dateModel, setDateModel] = useState({
    hour: '',
    minute: '',
    date: new Date().toISOString(),
    utcDate: '',
    validHour: true,
    validMinutes: true,
    validDate: true,
    boxError: false,
    showFeedback: false
  })

  const initDate = useCallback(() => {
    const m = moment
      .utc(scheduleDate || new Date().toISOString())
      .tz(zones[timeZone])

    const min = roundToClosestMultiple(m.minutes(), INPUT_MINUTES_STEP)
    m.set('minutes', min > 55 ? 55 : min)

    !scheduleDate && m.add(5, 'minutes')

    setDateModel(prev => ({
      ...prev,
      date: m.format('YYYY-MM-DD'),
      hour: m.hours(),
      minute: m.minutes(),
      utcDate: scheduleDate || new Date().toISOString()
    }))

    return m
  }, [])

  useEffect(() => {
    initDate()
  }, [])

  const handleChangeDate = jsDate => {
    if (jsDate instanceof Date && !isNaN(jsDate)) {
      const date = getLocaleDate(jsDate)

      setValidDiff({
        d: date,
        h: dateModel.hour,
        m: dateModel.minute
      })

      setDateModel(prev => ({
        ...prev,
        date
      }))
    }
  }

  useEffect(() => {
    if (selectTimezone !== '') {
      // nuova utc date in base alla zona vecchia
      const newUtcDateFromOldTimeZone = moment.tz(
        `${dateModel.date} ${dateModel.hour}:${dateModel.minute}`,
        'YYYY-MM-DD HH:mm',
        zones[oldTimeZone]
      )
      const newBaseUtcDate = new Date(
        newUtcDateFromOldTimeZone.utc()
      ).toISOString()
      const m = moment.utc(newBaseUtcDate).tz(zones[selectTimezone])
      setOldTimeZone(selectTimezone)

      const minute = roundToClosestMultiple(m.minutes(), INPUT_MINUTES_STEP)
      setValidDiff({
        d: m.format('YYYY-MM-DD'),
        h: m.hours(),
        m: minute
      })

      setDateModel(prev => ({
        ...prev,
        date: m.format('YYYY-MM-DD'),
        hour: m.hours(),
        minute: minute,
        utcDate: newBaseUtcDate
      }))
    }
  }, [selectTimezone])

  const handleChangeTimeZone = value => {
    setTimeZone(value)
    // la select mantiene i dati "immutabili"
    setSelectTimezone(value)
  }

  const setValidDiff = ({ d, h, m }) => {
    const actualDate = moment.tz(
      `${d} ${h}:${m}`,
      'YYYY-MM-DD HH:mm',
      zones[timeZone]
    )
    const actualDateInUtc = actualDate.clone().utc()
    const baseUtcDate = moment.tz(zones[timeZone]).utc()
    const minutes = roundToClosestMultiple(
      +baseUtcDate.format('mm'),
      INPUT_MINUTES_STEP
    )
    // i muniti devono essere arrotondati
    baseUtcDate.set('minutes', minutes + INPUT_MINUTES_STEP)
    baseUtcDate.set('seconds', 0)

    // sempre valido
    const validDate = actualDateInUtc.isAfter(baseUtcDate)
    if (validDate) {
      setDateModel(prev => ({
        ...prev,
        validHour: true,
        validMinutes: true,
        validDate: true,
        boxError: false,
        showFeedback: false
      }))
      return true // valido
    } else {
      const validH =
        actualDateInUtc.diff(baseUtcDate, 'hours') >= 0 &&
        actualDateInUtc.hours() >= baseUtcDate.hours()
      const validM = actualDateInUtc.diff(baseUtcDate, 'minutes') >= 0
      const validD =
        actualDate.diff(baseUtcDate, 'days') >= 0 &&
        actualDate.day() >= baseUtcDate.day()

      setDateModel(prev => ({
        ...prev,
        validHour: validH,
        validMinutes: validM,
        validDate: validD,
        showFeedback: !(validH && validM && validD)
        // boxError: !(validH && validM && validD)
      }))

      return validH && validM && validD // return true if date is valid
    }
  }

  const handleBlurHours = e => {
    e.persist()
    const value = +e.target.value
    setValidDiff({
      d: dateModel.date,
      h: value,
      m: dateModel.minute
    })
    setDateModel(prev => ({
      ...prev,
      hour: value
    }))
  }

  const handleChangeHours = value => {
    if (typeof value !== 'number' || value === '' || value > 23 || value < 0) {
      setDateModel(prev => ({
        ...prev,
        validHour: false,
        showFeedback: false
      }))
    } else {
      setValidDiff({
        d: dateModel.date,
        h: value,
        m: dateModel.minute
      })
      setDateModel(prev => ({
        ...prev,
        hour: value
      }))
    }
  }

  const handleBlurMinutes = value => {
    setValidDiff({
      d: dateModel.date,
      h: dateModel.hour,
      m: dateModel.minute
    })
  }

  const handleChangeMinutes = value => {
    if (typeof value !== 'number' || value === '' || value >= 60 || value < 0) {
      setDateModel(prev => ({
        ...prev,
        validMinutes: false,
        showFeedback: false
      }))
    } else {
      const minute = roundToClosestMultiple(value, INPUT_MINUTES_STEP)
      setValidDiff({
        d: dateModel.date,
        h: dateModel.hour,
        m: minute === 60 ? 55 : minute
      })
      setDateModel(prev => ({
        ...prev,
        minute: minute === 60 ? 55 : minute
      }))
    }
  }

  const handleUtcDate = () => {
    const d = moment.tz(
      `${dateModel.date} ${dateModel.hour}:${dateModel.minute}`,
      'YYYY-MM-DD HH:mm',
      zones[timeZone]
    )
    d.utc()
    return d
  }

  const handleConfirm = () => {
    const ret = setValidDiff({
      d: dateModel.date,
      h: dateModel.hour,
      m: dateModel.minute
    })
    if (ret) {
      confirm(handleUtcDate(), timeZone).then(e => {
        if (e) {
          history.push('/messages/email/sendstatus/campaigns')
        }
      })
    }
  }

  const isValidMinutes = () => {
    return typeof dateModel.minute === 'number' && dateModel.validMinutes
  }

  const isValidHours = () => {
    return typeof dateModel.hour === 'number' && dateModel.validHour
  }

  const getFeedback = () => {
    let retStr = null
    if (dateModel.showFeedback) {
      retStr = intl.formatMessage({
        id: 'Load.Common.ValidationScheduledTime'
      })
    } else {
      if (!(isValidMinutes() && isValidHours())) {
        retStr = intl.formatMessage({
          id: 'Load.Common.InvalidTime'
        })
      }
    }

    return retStr
  }

  const getCurrentDate = () => {
    const momentUTCDate = moment
      .tz(
        `${dateModel.date} ${dateModel.hour}:${dateModel.minute}`,
        'YYYY-MM-DD HH:mm',
        zones[timeZone]
      )
      .utc()

    return new Date(
      new Date(momentUTCDate.format()).toLocaleString('en-US', {
        timeZone: zones[timeZone]
      })
    )
  }

  const getMinDate = () => {
    return new Date(
      new Date().toLocaleString('en-US', {
        timeZone: zones[timeZone]
      })
    )
  }

  return (
    <>
      <ModalBody>
        <p>
          {dateModel.boxError && (
            <MessageBox
              variant={MESSAGE_BOX_VARIANT_DANGER}
              closeButton
              closeButtonCallback={() => {
                setDateModel(prev => ({
                  ...prev,
                  boxError: false
                }))
              }}
              data-mu-test='message-box-error'
            >
              <>
                {intl.formatMessage({
                  id: 'Load.Common.ValidationScheduledDateTime'
                })}
              </>
            </MessageBox>
          )}
        </p>
        <p>
          {intl.formatMessage(
            {
              id: 'Load.Schedulation.SendTypeScheduledText'
            },
            {
              strong: chunks => <strong>{chunks}</strong>
            }
          )}
        </p>
        <div className='b-modal-schedule__form'>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblData'
            })}
            feedback={
              !dateModel.validDate
                ? intl.formatMessage({
                    id: 'Load.Common.ValidationScheduledDate'
                  })
                : null
            }
          >
            <DatePicker
              value={getCurrentDate()}
              callback={handleChangeDate}
              minDate={getMinDate()}
              invalid={!dateModel.validDate}
            />
          </FormElement>
          <FormElement
            feedback={getFeedback()}
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblOra'
            })}
          >
            <InputNumber
              defaultValue={10}
              placeholder='HH'
              value={dateModel.hour}
              onChange={handleChangeHours}
              min={0}
              max={23}
              onBlur={handleBlurHours}
              variant={isValidHours() ? '' : 'danger'}
            />

            <InputNumber
              step={INPUT_MINUTES_STEP}
              placeholder='MM'
              value={dateModel.minute}
              onChange={handleChangeMinutes}
              min={0}
              max={55}
              onBlur={handleBlurMinutes}
              variant={isValidMinutes() ? '' : 'danger'}
            />
          </FormElement>
          <FormElement
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblFuso'
            })}
          >
            <Select
              value={timeZone}
              onChange={handleChangeTimeZone}
              searchable
              options={Object.entries(zones).map(([code, label]) => ({
                value: code,
                label: `(${code.replace(/([^.]+)\.\d+$/, '$1')}) ${label}`
              }))}
            />
          </FormElement>
        </div>
      </ModalBody>
      <Foot
        disabled={!(isValidMinutes() && isValidHours() && dateModel.validDate)}
        onClickCancel={cancel}
        onClickConfirm={handleConfirm}
      />
    </>
  )
}
